import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@shopflo/ui/icon";
import { Popover, PopoverTrigger, PopoverContent, PopoverClose } from "@shopflo/ui/popover";
import { Plus, CheckCircle, DotsThreeVertical } from "@phosphor-icons/react";
import { useUserContext } from "lib/contexts/UserProvider";
import { staticOptions } from "lib/core/apiClient";
import EmailDialog from "./EmailDialog";
import { IconButton } from "@shopflo/ui/icon-button";
import DeleteConfirmationPopup from "components/common/dialogs/DeleteConfirmationPopup";
import {
  CustomerEmail,
  getLoginEmailsFromAccount,
  deleteCustomerEmail,
  setPrimaryCustomerEmail,
} from "lib/utils/profileEmails";
import { AUTHENTICATE_ME_PATH, fetchAuthenticateMe } from "lib/utils/authenticateMe";
import { getCheckoutUiRootElement } from "lib/utils/helpers";
import { bannerErrorToastV2, bannerSuccessToastV2 } from "lib/utils/toasters";
import useSWR from "swr";
import { useLocale } from "lib/hooks/useLocale";

const LetterAvatar = ({ letter }: { letter: string }) => (
  <div className="flex items-center justify-center w-7 h-7 rounded-full text-yay-dark bg-emerald-50 border-1 border-zinc-950/5">
    <div className="text-text-xs-bold [text-box-trim:trim-both]">
      {letter.toUpperCase()}
    </div>
  </div>
);

const LoginEmails = () => {
  const { t } = useLocale();
  const {
    state: { user },
    actions: { updateUser },
  } = useUserContext();

  /** Same key + fetcher as UserProvider → one `/authenticate/me` request, shared SWR cache. */
  const { data: accountPayload, isValidating, error } = useSWR(
    AUTHENTICATE_ME_PATH,
    fetchAuthenticateMe,
    staticOptions,
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit" | "verify">("add");
  const [editingEmail, setEditingEmail] = useState("");
  const [pendingDeleteEmail, setPendingDeleteEmail] = useState<string | null>(null);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [isDeletingEmail, setIsDeletingEmail] = useState(false);

  useEffect(() => {
    if (!error) {
      return;
    }
    bannerErrorToastV2(t("profile_emails_load_error"));
  }, [error, t]);

  const loginEmailsFromAccount = useMemo(
    () => (accountPayload ? getLoginEmailsFromAccount(accountPayload) : []),
    [accountPayload],
  );

  const isLoadingEmails = Boolean(isValidating && !accountPayload);

  const emailsToRender = useMemo(() => {
    if (loginEmailsFromAccount.length > 0) {
      return loginEmailsFromAccount;
    }

    if (!user?.email) {
      return [];
    }

    return [
      {
        email: user.email,
        primary: true,
        verified: false,
      },
    ] as CustomerEmail[];
  }, [loginEmailsFromAccount, user?.email]);

  const effectivePrimaryEmail = useMemo(() => {
    const primaryEmail = emailsToRender.find((item) => item.primary);
    if (primaryEmail) {
      return primaryEmail.email;
    }

    const firstVerified = emailsToRender.find((item) => item.verified);
    return firstVerified?.email ?? "";
  }, [emailsToRender]);

  const orderedEmails = useMemo(() => {
    if (!effectivePrimaryEmail) {
      return emailsToRender;
    }

    const primaryItem = emailsToRender.find((item) => item.email === effectivePrimaryEmail);
    if (!primaryItem) {
      return emailsToRender;
    }

    const remainingItems = emailsToRender.filter((item) => item.email !== effectivePrimaryEmail);
    return [primaryItem, ...remainingItems];
  }, [emailsToRender, effectivePrimaryEmail]);

  const handleAddEmail = () => {
    setDialogMode("add");
    setEditingEmail("");
    setIsDialogOpen(true);
  };

  const handleEditEmail = (email: string) => {
    setDialogMode("edit");
    setEditingEmail(email);
    setIsDialogOpen(true);
  };

  const handleVerifyEmail = (email: string) => {
    setDialogMode("verify");
    setEditingEmail(email);
    setIsDialogOpen(true);
  };

  const handleDeleteEmailClick = (email: string) => {
    setPendingDeleteEmail(email);
    setIsDeletePopupOpen(true);
  };

  const deleteEmail = async (email: string) => {
    const isPrimary = email === effectivePrimaryEmail;
    try {
      setIsDeletingEmail(true);
      await deleteCustomerEmail(email);
      updateUser();
      bannerSuccessToastV2(
        isPrimary ? t("profile_toast_login_email_removed_success") : t("profile_toast_email_removed_success"),
      );
    } catch {
      bannerErrorToastV2(isPrimary ? t("profile_error_remove_login_email") : t("profile_error_remove_email"));
    } finally {
      setIsDeletingEmail(false);
      setIsDeletePopupOpen(false);
    }
  };

  const setAsPrimary = async (email: string) => {
    try {
      await setPrimaryCustomerEmail(email);
      updateUser();
      bannerSuccessToastV2(t("profile_toast_default_login_email_updated"));
    } catch {
      bannerErrorToastV2(t("profile_error_update_default_login_email"));
    }
  };

  const handleSetAsLoginEmail = async (item: CustomerEmail) => {
    if (!item.verified) {
      handleVerifyEmail(item.email);
      return;
    }
    await setAsPrimary(item.email);
  };

  const handleDialogSuccess = useCallback(() => {
    updateUser();
  }, [updateUser]);

  const handleCloseEmailDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const closeDeleteConfirmation = () => {
    setIsDeletePopupOpen(false);
  };

  useEffect(() => {
    if (isDeletePopupOpen || pendingDeleteEmail === null) return;

    const id = window.setTimeout(() => {
      setPendingDeleteEmail(null);
    }, 320);

    return () => window.clearTimeout(id);
  }, [isDeletePopupOpen, pendingDeleteEmail]);

  const checkoutPortalRoot = getCheckoutUiRootElement();

  const deleteEmailConfirmationCopy = useMemo(() => {
    if (!pendingDeleteEmail) {
      return { description: "", title: "" };
    }
    const isPrimaryRow = pendingDeleteEmail === effectivePrimaryEmail;
    return {
      title: isPrimaryRow ? t("profile_delete_email_title_primary") : t("profile_delete_email_title_secondary"),
      description: isPrimaryRow
        ? t("profile_delete_email_desc_primary", { email: pendingDeleteEmail })
        : t("profile_delete_email_desc_secondary", { email: pendingDeleteEmail }),
    };
  }, [pendingDeleteEmail, effectivePrimaryEmail, t]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-text-sm-semibold text-zinc-900">{t("profile_emails_header")}</h2>
        <p className="text-text-xs-regular text-zinc-500">{t("profile_emails_subtitle")}</p>
      </div>

      <div className="flex flex-col px-3 py-1 bg-zinc-500/5 rounded-xl">
        <button
          className="flex items-center gap-2 py-2.5 border-b border-zinc-950/5 cursor-pointer transition-colors"
          onClick={handleAddEmail}>
          <div className="flex items-center justify-center px-1.5 py-1 rounded-full border-zinc-300">
            <Icon IconComponent={Plus} size="xs" className="text-indigo-600" />
          </div>
          <span className="text-text-sm-semibold text-indigo-600">{t("profile_emails_add_new")}</span>
        </button>
        <div className="pr-1">
          {isLoadingEmails && emailsToRender.length === 0 ? (
            <div className="py-3 text-text-sm-regular text-zinc-500">{t("profile_emails_loading")}</div>
          ) : null}

          {!isLoadingEmails && emailsToRender.length === 0 ? (
            <div className="py-3 text-text-sm-regular text-zinc-500">{t("profile_emails_empty")}</div>
          ) : null}

          {orderedEmails.map((item) => {
            const isEffectivePrimary = item.email === effectivePrimaryEmail;
            const [emailLocal = item.email, emailDomain = ""] = item.email.split("@");

            return (
              <div key={item.email} className="flex items-center justify-between py-2.5 rounded-lg transition-colors">
                <div className="flex items-center gap-2">
                  <LetterAvatar letter={item.email.charAt(0)} />
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-text-sm-medium text-zinc-800">{emailLocal}</span>
                      {emailDomain ? (
                        <span className="text-text-sm-regular text-zinc-500">@{emailDomain}</span>
                      ) : null}
                      {isEffectivePrimary && (
                        <Icon
                          IconComponent={CheckCircle}
                          size="xs"
                          className="text-green-600"
                          iconProps={{ weight: "fill" }}
                        />
                      )}
                    </div>
                    {isEffectivePrimary && (
                      <span className="text-text-xs-regular text-zinc-500">{t("profile_emails_current_login")}</span>
                    )}
                  </div>
                </div>

                <Popover>
                  <PopoverTrigger asChild>
                    <IconButton IconComponent={DotsThreeVertical} size="sm" variant="ghost" />
                  </PopoverTrigger>
                  <PopoverContent align="end" className="max-w-[210px]">
                    <div className="flex flex-col gap-1">
                      <PopoverClose asChild>
                        <button
                          className="text-text-sm-regular text-zinc-600 px-3 py-1.5 rounded-md text-left cursor-pointer hover:bg-zinc-50 hover:text-zinc-800 transition-colors"
                          onClick={() => handleEditEmail(item.email)}>
                          {t("edit")}
                        </button>
                      </PopoverClose>
                      {!isEffectivePrimary ? (
                        <PopoverClose asChild>
                          <button
                            className="text-text-sm-regular text-zinc-600 px-3 py-1.5 rounded-md text-left cursor-pointer hover:bg-zinc-50 hover:text-zinc-800 transition-colors"
                            onClick={() => handleSetAsLoginEmail(item)}>
                            {t("profile_emails_set_as_login")}
                          </button>
                        </PopoverClose>
                      ) : null}
                      <PopoverClose asChild>
                        <button
                          className="text-text-sm-regular text-zinc-600 px-3 py-1.5 rounded-md text-left cursor-pointer hover:bg-zinc-50 hover:text-zinc-800 transition-colors"
                          onClick={() => handleDeleteEmailClick(item.email)}>
                          {t("profile_emails_delete")}
                        </button>
                      </PopoverClose>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            );
          })}
        </div>
      </div>
      <EmailDialog
        isOpen={isDialogOpen}
        onClose={handleCloseEmailDialog}
        onSuccess={handleDialogSuccess}
        mode={dialogMode}
        initialEmail={editingEmail}
        isPrimaryEmail={effectivePrimaryEmail === editingEmail}
      />
      <DeleteConfirmationPopup
        isOpen={isDeletePopupOpen}
        actions={{
          onConfirm: () => {
            if (!pendingDeleteEmail) return;
            void deleteEmail(pendingDeleteEmail);
          },
          onClose: closeDeleteConfirmation,
        }}
        config={{
          title: deleteEmailConfirmationCopy.title,
          description: deleteEmailConfirmationCopy.description,
          confirmationText: isDeletingEmail ? t("profile_delete_confirm_loading") : t("remove"),
          cancelLabel: t("profile_delete_email_keep"),
          isLoading: isDeletingEmail,
        }}
        portalContainer={checkoutPortalRoot}
      />
    </div>
  );
};

export default LoginEmails;
